import { getPdfTitle } from '@features/logs/utils/incidentUtils'
import type { IncidentReport } from '@lmc/ts-common'
import { Button } from '@lmc/ui-kit/src/general'
import { showToast } from 'common/utils'
import { downloadPdfDocument } from 'components/pdf/document/renderPdfDocument'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CaseMetadata } from '../view/readRules'
import useIncidentCaseV2Document from './useIncidentCaseV2Document'

type IncidentCaseV2PdfButtonProps = {
  incidentReport: IncidentReport
  testId: string
}

/**
 * Downloads a v2 case as a PDF built from its answers, not a screenshot of
 * the page. Disabled until the names it prints have loaded, and while a PDF
 * is being made, so a second click can't make a second file.
 */
const IncidentCaseV2PdfButton = ({
  incidentReport,
  testId
}: IncidentCaseV2PdfButtonProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { buildDocument, isError, isLoading } =
    useIncidentCaseV2Document(incidentReport)
  const [isDownloading, setIsDownloading] = useState(false)
  // State updates after the click's render; this blocks a click before it.
  const isInFlight = useRef(false)
  const { caseReference } = incidentReport as IncidentReport & CaseMetadata
  const title = getPdfTitle(
    incidentReport,
    t(incidentReportsConstants.INCIDENT_CASE)
  )
  const fileName = caseReference
    ? t(incidentReportsConstants.CASE_V2_PDF_FILE_NAME, {
        reference: caseReference,
        title
      })
    : title

  const showError = () =>
    showToast({
      title: t(generalConstants.COULDNT_DOWNLOAD_PDF, {
        ns: NAMESPACE.GENERAL
      }),
      type: 'error'
    })

  const onClick = async () => {
    if (isInFlight.current) {
      return
    }
    // A name that didn't load would be missing from the PDF.
    if (isError) {
      showError()
      return
    }
    isInFlight.current = true
    setIsDownloading(true)
    try {
      await downloadPdfDocument(buildDocument(), fileName)
    } catch {
      showError()
    } finally {
      isInFlight.current = false
      setIsDownloading(false)
    }
  }

  return (
    <Button
      data-testid={testId}
      disabled={isLoading || isDownloading}
      isLoading={isDownloading}
      onClick={onClick}
    >
      {t(incidentReportsConstants.DOWNLOAD_PDF)}
    </Button>
  )
}

export default IncidentCaseV2PdfButton
